import {
  Field,
  FieldItemProps,
  Group,
  RecordItem,
  SimpleFieldItemProps,
} from './RecordPreviewProps';
import { NuvoLink } from '@nuvolo/nuux-core/components/NuvoLink';

const mapField = <T,>(
  field: Field<T>,
  record: T,
  navigate?: (url: string) => void,
): SimpleFieldItemProps => {
  const { label, render, onClick, dataType, cssClass, url, ...rest } = field;
  let result: SimpleFieldItemProps = {
    ...rest,
    itemType: 'simple',
    cssClass: cssClass,
    render,
    label,
    visible: true,
  };

  record[field.dataField as keyof T] === null || record[field.dataField as keyof T] === undefined
    ? (result = { ...result, value: 'Unknown' })
    : (result = { ...result, value: record[field.dataField as keyof T] });

  if (dataType === 'date') {
    result = {
      ...result,
      value: record[field.dataField as keyof T]
        ? new Date(result.value).toLocaleDateString()
        : 'Unknown',
    };
  }
  if (dataType === 'datetime') {
    result = {
      ...result,
      value: record[field.dataField as keyof T]
        ? new Date(result.value).toLocaleString()
        : 'Unknown',
    };
  }
  if (dataType === 'link' && record[field.valueExpr as keyof T]) {
    const handleClick = () => {
      if (url && navigate) {
        navigate(url.replace(':id', record[field.valueExpr as keyof T] as string));
      }
    };

    result = {
      ...result,
      render: () => (
        <NuvoLink type="large" onClick={handleClick}>
          {result.value}
        </NuvoLink>
      ),
    };
  }
  if (dataType === 'list') {
    let listValue: { [key in keyof T]: string }[] = [];
    if (typeof record[field.dataField as keyof T] === 'string') {
      try {
        listValue = JSON.parse(record[field.dataField as keyof T] as string);
      } catch {
        listValue = [];
      }
    }
    if (Array.isArray(listValue) && listValue.length > 0) {
      result = {
        ...result,
        render: () => (
          <ul className="list-field-ul">
            {listValue.map((item, index) => (
              <li key={index}>{item[field.valueExpr as keyof T]}</li>
            ))}
          </ul>
        ),
      };
    } else {
      result = { ...result, value: 'Unknown' };
    }
  }
  if (field.dataField.split('.').length > 1) {
    const dataFieldParts = field.dataField.split('.');
    const value = dataFieldParts.reduce(
      (obj: Record<string, unknown>, key: string) => obj?.[key] as Record<string, unknown>,
      record as Record<string, unknown>,
    );

    result = {
      ...result,
      value: value || 'Unknown',
      cssClass: value && 'link-field',
      visible: !!value,
    };
  }
  return result;
};

const mapGroup = <T,>(
  group: Group<T>,
  record: T,
  navigate?: (url: string) => void,
): FieldItemProps<T> => {
  const { items, ...rest } = group;

  const result: FieldItemProps<T> = {
    ...rest,
    items: mapFieldItems(items ?? [], record, navigate),
    itemType: 'group',
  };

  return result;
};

const isGroup = <T,>(item: RecordItem<T>): item is Group<T> => {
  return 'items' in item;
};

const mapFieldItem = <T,>(
  item: RecordItem<T>,
  record: T,
  navigate?: (url: string) => void,
): FieldItemProps<T> =>
  isGroup(item) ? mapGroup(item, record, navigate) : mapField(item, record, navigate);

const mapFieldItems = <T,>(
  items: RecordItem<T>[],
  record: T,
  navigate?: (url: string) => void,
): FieldItemProps<T>[] => items.map((item) => mapFieldItem(item, record, navigate));

export default mapFieldItems;
